import { useCallback, useEffect, useState } from 'react';
import { errorMessage } from '../api/client';
import { formatSize } from '../lib/format';
import { desktop, isDesktop } from './bridge';
import { useSyncStatus } from './useSyncStatus';

/**
 * Emporter les rapports d'un poste, sur le modèle d'une liste de lecture mise
 * hors ligne : un bouton, pas un choix fichier par fichier.
 *
 * Le retrait supprime les fichiers. Demandé alors que le poste est hors
 * ligne, il demande confirmation : les fichiers ne pourront pas être
 * récupérés avant le retour du réseau, et un clic distrait coûterait la
 * consultation d'un rapport dans un local technique sans couverture.
 *
 * Rend `null` dans le navigateur, où il n'y a pas de disque à remplir.
 */
export function FollowReportsButton({
  workstationId,
  workstationName,
}: {
  workstationId: string;
  workstationName: string;
}) {
  const status = useSyncStatus();
  const [followed, setFollowed] = useState<boolean | null>(null);
  const [summary, setSummary] = useState<{ files: number; missing: number; bytes: number } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback((): void => {
    if (!isDesktop()) return;
    const bridge = desktop();
    Promise.all([bridge.reports.isFollowed(workstationId), bridge.reports.usage()])
      .then(([isFollowed, usage]) => {
        setFollowed(isFollowed);
        const entry = usage.workstations.find(
          (candidate) => candidate.workstationId === workstationId,
        );
        setSummary(
          entry === undefined
            ? null
            : { files: entry.fileCount, missing: entry.missingCount, bytes: entry.sizeBytes },
        );
      })
      .catch((cause: unknown) => setError(errorMessage(cause)));
  }, [workstationId]);

  useEffect(reload, [reload]);
  // les fichiers descendent après une synchronisation : on relit le décompte
  useEffect(reload, [reload, status?.lastSyncAt]);

  if (!isDesktop() || followed === null) return null;

  const act = (run: Promise<unknown>): void => {
    setBusy(true);
    setError(null);
    run
      .then(reload)
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setBusy(false));
  };

  const onClick = (): void => {
    if (!followed) {
      act(desktop().reports.follow(workstationId));
      return;
    }

    if (status?.state === 'horsLigne') {
      const confirme = window.confirm(
        `Ce poste est hors ligne. Retirer le suivi de « ${workstationName} » ` +
          'supprime ses rapports du disque, et ils ne pourront pas être ' +
          'récupérés avant le retour du réseau.\n\nRetirer quand même ?',
      );
      if (!confirme) return;
    }
    act(desktop().reports.unfollow(workstationId));
  };

  return (
    <div className="ligne">
      <button
        type="button"
        className={followed ? 'bouton' : 'bouton bouton--primaire'}
        onClick={onClick}
        disabled={busy}
        title={
          followed
            ? 'Supprime les rapports de ce poste du disque de cette machine.'
            : 'Télécharge les rapports de ce poste, et les suivants tant que le suivi dure.'
        }
      >
        {followed ? 'Ne plus emporter les rapports' : 'Emporter les rapports'}
      </button>
      {followed && summary !== null && (
        <span className="faible">
          {summary.files} fichier(s) · {formatSize(summary.bytes)}
          {summary.missing > 0 && ` · ${summary.missing} à descendre au retour du réseau`}
        </span>
      )}
      {error !== null && <span className="faible">{error}</span>}
    </div>
  );
}
